import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS type
import { NotIncludeObj, IalreadyFetchedGDSMonth} from '@redux/common';
import { RootState } from '@redux/store';

// import module
import IcRcln from '@magaele/ic_rcln';

// component
import { Calendar } from '@lion-design/core';

// common function
import { convertTicketNum, addSeparator, getSessionInfo } from '@libs/core';
import { isMobile, calendarLoc } from '@libs/globalVariables';
import { fetchGDSDetailAnotherMonthData, setGDSSessionData } from '@libs/apiCall';

// plugin
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setCalendar,
    setGCalenderErr,
    setGSessionErr,
    setGTempCalendarData,
    setGTicketErr,
    setTicketNum,
    setIsGDSSessionLoading,
    setGDSSessionFetchFail,
    setSession,
    setSessionPicked,
    setGDSDetailFetchFail,
    setGDetail,
    setIsGDSDetailLoading,
} from '@redux/GDS/actions';
import { setGDetailStore, setDetailFetchFailMsg } from '@redux/Common/actions';

interface IGCalendarProps {
    alreadyFetchedGDSMonth?: IalreadyFetchedGDSMonth;
}
interface loopDates {
    (start: string, end: string): string[];
}

const CalendarBlock: React.FC<IGCalendarProps> = (props) => {
    // console.log('CalendarBlock', props);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const {
        GDetail,
        GTempCalendarData,
        calendar,
        ticketNum,
        GCalenderErr,
        GSessionErr,
        GTicketErr,
        GNowListData,
        isGDSSessionLoading,
        session,
        sessionPicked,
    } = useSelector((state: RootState) => state.GDS);

    const { Language, isLogin, ETID, GDetailStore } = useSelector(
        (state: RootState) => state.Common
    );
    const { FormatID, EndDate } = GNowListData;
    const { LastBuyDay } = GDetail;
    // 如果沒有可賣日期將今天min && max設為當日再用disabled date扣掉今天
    const min: string = GDetail?.EarliestBuyDay || dayjs().format('YYYY-MM-DD'); 
    // console.log('min~~~', min);
    const max: string = (() => {
        if (!LastBuyDay) return dayjs().format('YYYY-MM-DD');
        return LastBuyDay;
    })();
    // console.log('max', max);
    const noAvailableDays = !GDetail?.EarliestBuyDay && !EndDate;
    const notIncludes = GDetail.NotIncludeDate;
    const allCalendarData = GTempCalendarData;
    const calendarlang = useMemo(() => { // 多語系月曆 月份&星期文字使用的資料
        let lang: string;
        // 香港語系翻譯檔與台灣相同
        Language === 'zh-hk' ? lang = 'zh-tw' : lang = Language;
        const newMonthsArr = calendarLoc[lang].months.slice(1); // 移除空值
        return {
            month: newMonthsArr,
            weekday: calendarLoc[lang].weekdays,
        }
    },[calendarLoc])
    
    // 備用 自己算最低價，目前可先抓LowestPrice
    const initLowSalePrice = (date: string) => {
        const prices = GDetail.CalendarDate[date]?.ETItem.map((item) => item.SalePrice);
        if (!prices) return null;
        return Math.min(...prices);
    };
    const priceSelectedArray = (ticket: { [key: string]: number }, date: string) => {
        const priceStore = [];
        // const newticket = convertTicketNum(allCalendarData[date]?.ETItem);
        const shouldUseDefaultETITEM =
            !GDetail.Sessions || isGDSSessionLoading || date !== calendar;
        const dataPerDate = shouldUseDefaultETITEM
            ? allCalendarData[date]
            : getSessionInfo({ session, sessionPicked });
        const _lowestPrice = shouldUseDefaultETITEM
            ? GDetail.CalendarDate[date]?.LowestPrice
            : getSessionInfo({ session, sessionPicked })?.LowestPrice;

        const newticket = convertTicketNum(dataPerDate?.ETItem);

        if (date !== calendar || (GDetail.Sessions && isGDSSessionLoading)) {
            return _lowestPrice;
            // return GDetail.CalendarDate[date]?.LowestPrice;
            // return initLowSalePrice(date);  // 如果LowestPrice有問題就用這個function回傳的
        } else {
            dataPerDate?.ETItem?.forEach((item) => {
                let tempTicket = newticket;
                if (date === calendar && ticketNum) tempTicket = ticketNum;
                const selectQty = tempTicket[item.ItemSeq];
                
                let price = isLogin
                ? item.SalePrice !== 0
                    ? 'SalePrice'
                    : 'SalePrice_NonMember'
                : 'SalePrice_NonMember';
                if (selectQty !== 0) {
                    if (priceStore.indexOf(item[price]) === -1) priceStore.push(item[price]);
                }
            });
        }
        if (priceStore.length === 0) {
            return _lowestPrice;
            // return GDetail.CalendarDate[date]?.LowestPrice;
            // return initLowSalePrice(date); // 如果LowestPrice有問題就用這個function回傳的
        } else {
            return Math.max(...priceStore);
        }
    };

    // 將日期區間之日子loop出來
    const generateDates = (_min: string, _max: string) => {
        if (!_min || !_max) return [];
        const daysLength = dayjs(_max).diff(_min, 'day') + 1;
        const rangeDates = [];
        for (let i = 0; i < daysLength; i++) {
            rangeDates.push(dayjs(_min).add(i, 'day').format('YYYY-MM-DD'));
        }
        return rangeDates;
    };

    // 備用function 如果notInclude date有問題就自己算
    const noIncludeDates = (dateArr: string[]) => {
        const daysLength = dayjs(max).diff(min, 'day') + 1;
        const notInclude = [];
        for (let i = 0; i < daysLength; i++) {
            const loopDate = dayjs(min).add(i, 'day').format('YYYY-MM-DD');
            if (dateArr.indexOf(loopDate) === -1) notInclude.push(loopDate);
        }
        // 將回傳回來錯誤資料篩掉(有回傳但是etitem卻為空陣列)
        dateArr?.forEach((item) => {
            if (GDetail.CalendarDate[item]?.ETItem?.length === 0) notInclude.push(item);
        });
        return notInclude;
    };

    // 客製不可選日子陣列
    const disabledDates = ((dates: NotIncludeObj[], callback: loopDates) => {
        if (noAvailableDays) return [dayjs().format('YYYY-MM-DD')]; // 如果沒有可賣日期將今天設為disabled

        // if (!dates) {
        //     // 備用 如果沒有傳notInclude date就自己算吧...
        //     const includeDate = Object.keys(GDetail.CalendarDate);
        //     const disabledDates = noIncludeDates(includeDate);
        //     return disabledDates;
        // }
        const allDisabled = [];

        // 備用 自己將0元也設進disabled
        // if (GDetail.CalendarDate) {
        //     const includeDate = Object.keys(GDetail.CalendarDate);
        //     if (includeDate.length !== 0) {
        //         includeDate?.map((item) => {
        //             if (GDetail.CalendarDate[item].LowestPrice === 0) {
        //                 allDisabled.push(item);
        //             }
        //         });
        //     }
        // }

        dates?.forEach((item: NotIncludeObj) => {
            if (!!item) {
                allDisabled.push(...callback(item.StartDate, item.EndDate));
            }
        });
        return allDisabled;
    })(notIncludes, generateDates);

    // 可選日子陣列
    const ableDates = ((oriDates: string[], disableDates: string[]) => {
        const sortDate = oriDates.filter(function (dates) {
            return this.indexOf(dates) < 0;
        }, disableDates);
        return sortDate;
    })(generateDates(min, max), disabledDates);

    // 月曆上所有可選日子資訊物件
    const detailOfDates = ((_ableDates) => {
        const dateNote = (_price: any, isSingle: boolean) => {
            let finalPrice = _price && addSeparator(_price);
            if (isMobile && _price && finalPrice.length >= 5) {
                const tempPrice = finalPrice.split(',').join('');
                finalPrice = tempPrice.substring(0, tempPrice.length - 3) + 'k+';
            }
            if (!isMobile || !isSingle) {
                finalPrice += t('noTHSR.calendar.up');
            }
            return (
                <>
                    <span>{_price && finalPrice}</span>
                </>
            );
        };
        const detailArr = {};
        ableDates.forEach((date) => {
            const ETitemPerday = allCalendarData[date]?.ETItem;
            const isSingle = allCalendarData[date]?.ETItem.length === 1;
            if (ETitemPerday) {
                const finalPrice = priceSelectedArray(ticketNum, date);
                detailArr[date] = { note: dateNote(finalPrice, isSingle) };
            } else {
                detailArr[date] = { note: dateNote(null, isSingle) };
            }
        });
        return detailArr;
    })(ableDates);
    
    //點選月曆
    const onClickDate = (date: string) => {
        // console.log('clicked Date', date);
        const tickets = convertTicketNum(GDetail.CalendarDate[date]?.ETItem);
        dispatch(setGTempCalendarData(GDetail.CalendarDate));
        dispatch(setTicketNum(tickets));
        dispatch(setCalendar(date));
        GDetail.Sessions &&
            setGDSSessionData({
                date,
                FormatID,
                ETID,
                context: {
                    setGSessionLoading: setIsGDSSessionLoading,
                    setGSessionFetchFail: setGDSSessionFetchFail,
                    setSession,
                    setSessionPicked,
                },
                dispatch,
            });
        GSessionErr && dispatch(setGSessionErr(''));
        GCalenderErr && dispatch(setGCalenderErr(''));
        GTicketErr && dispatch(setGTicketErr(''));
    };

    const isMinAfterMax = dayjs(min).isAfter(max);
    // 如果最早日比最晚日還晚(因工作日規則會往後順延startDate卻不會順延EndDate)，將此特殊情況設日曆為null
    isMinAfterMax && dispatch(setCalendar(null));

    const calendarProps = {
        pickDate: calendar, // 預設選取日期
        minDate: min,
        maxDate: max,
        disabledDay: disabledDates,
        detailDateData: detailOfDates,
        mustChooseOne: true, // 日期必選
        onChange: (date: string) => {
            // console.log('clicked Date', date);
            onClickDate(date);
        },
        onClickPrevMonth: (value: string) => {
            let prev = dayjs(value).subtract(1, 'M').format('YYYY-MM');
            // console.log('按下!!alreadyFetchedGDSMonth.current', alreadyFetchedGDSMonth.current);
         
            // if (Object.values(alreadyFetchedGDSMonth.current[FormatID]).findIndex((month) => month === prev) < 0) {
                const fetchDetailProps = {
                    ETID,
                    ExpectedUseDay: GNowListData?.ExpectedUseDay,
                    FormatID,
                    isTHSR: false,
                    context: {
                        GDetail,
                        setGDetail,
                        GDetailStore,
                        ThsrRoundType: null,
                        setGDetailStore,
                        setGTempCalendarData,
                        setIsGDSDetailLoading,
                        setGDSDetailFetchFail,
                        setDetailFetchFailMsg,
                        setCalendar,
                        setIsGDSSessionLoading,
                        setGDSSessionFetchFail,
                        setSession,
                        setSessionPicked,
                    },
                    //帶入月份
                    CalenderMonth: prev,
                    dispatch,
                };
                dispatch(setIsGDSSessionLoading(true));
                console.log('NEW CalenderMonth!!!', fetchDetailProps.CalenderMonth);
                fetchGDSDetailAnotherMonthData(fetchDetailProps);
            // }
        },
        onClickNextMonth: (value: string) => {
            let next = dayjs(value).add(1, 'M').format('YYYY-MM');
            // console.log('按下!!alreadyFetchedGDSMonth.current', alreadyFetchedGDSMonth.current);
            // if (Object.values(alreadyFetchedGDSMonth.current[FormatID]).findIndex((month) => month === next) < 0) {
                const fetchDetailProps = {
                    ETID,
                    ExpectedUseDay: GNowListData?.ExpectedUseDay,
                    FormatID,
                    isTHSR: false,
                    context: {
                        GDetail,
                        setGDetail,
                        GDetailStore,
                        ThsrRoundType: null,
                        setGDetailStore,
                        setGTempCalendarData,
                        setIsGDSDetailLoading,
                        setGDSDetailFetchFail,
                        setDetailFetchFailMsg,
                        setCalendar,
                        setIsGDSSessionLoading,
                        setGDSSessionFetchFail,
                        setSession,
                        setSessionPicked,
                    },
                    //帶入月份
                    CalenderMonth: next,
                    dispatch,
                };
                dispatch(setIsGDSSessionLoading(true));
                console.log('NEW CalenderMonth!!!', fetchDetailProps.CalenderMonth);
                fetchGDSDetailAnotherMonthData(fetchDetailProps);
                // console.log('onClickNextMonth', value);
            // }
        },
        arrowNextEle: <IcRcln name="angle-right-2" style={{ fontSize: '18px' }} />,
        arrowPrevEle: <IcRcln name="angle-right-2" style={{ fontSize: '18px' }} />,
        // 月曆預設顯示月份
        // curMonth: dayjs(dayjs(calendar).format('YYYY-MM')),
        isChineseMonth: Language === 'zh-tw' || Language === 'zh-hk'? true : false,
        isForeign: Language === 'zh-tw' || Language === 'zh-hk'? false : true,
        foreignCalendarText: calendarlang,
    };
    return <Calendar {...calendarProps} />;
};

export default CalendarBlock;